/**
 * Shared motion variants. One place to tune the page's "feel".
 *
 * Every preset has a reduced-motion fallback (opacity-only, near-instant) so
 * the page honours `prefers-reduced-motion` automatically — pass the value of
 * motion's `useReducedMotion()` into `makePresets`.
 */

import type { Variants } from "motion/react";

// Signature easing for the whole page (gentle overshoot-free ease-out).
const EASE: [number, number, number, number] = [0.2, 0.85, 0.25, 1];
const EASE_IN: [number, number, number, number] = [0.4, 0, 1, 1];

export interface Presets {
  /** Section panel enter/exit. */
  panel: Variants;
  /** Headings / paragraphs rising in, staggered by `delay` (seconds). */
  rise: (delay?: number) => Variants;
  /** Chips / cards popping in, staggered by `delay` (seconds). */
  pop: (delay?: number) => Variants;
  /** The oversized decorative section number. */
  bigNum: Variants;
}

export function makePresets(reduced: boolean): Presets {
  if (reduced) {
    const fade = (): Variants => ({
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: { duration: 0.2 } },
    });
    return {
      panel: {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
      },
      rise: fade,
      pop: fade,
      bigNum: { initial: { opacity: 0 }, animate: { opacity: 0.13, transition: { duration: 0.3 } } },
    };
  }

  return {
    panel: {
      initial: { opacity: 0, y: 26, scale: 0.965, rotate: -1.1 },
      animate: { opacity: 1, y: 0, scale: 1, rotate: 0, transition: { duration: 0.5, ease: EASE } },
      exit: { opacity: 0, y: -18, scale: 0.97, transition: { duration: 0.32, ease: EASE_IN } },
    },
    rise: (delay = 0) => ({
      initial: { opacity: 0, y: 16 },
      animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE, delay } },
    }),
    pop: (delay = 0) => ({
      initial: { opacity: 0, y: 10, scale: 0.92 },
      animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: EASE, delay } },
    }),
    bigNum: {
      initial: { opacity: 0, x: 48, y: -16, scale: 0.78, rotate: -16 },
      animate: { opacity: 0.13, x: 0, y: 0, scale: 1, rotate: 0, transition: { duration: 0.6, ease: EASE } },
    },
  };
}
